// Not ported (dead in the Angular app): getAccount
import { api, apiRequest } from '@/core/http/apiClient';
import { download } from '@/core/http/files';
import { exportFile, readBlobError } from '@/core/http/helpers';
import type {
  AccountingCallbackPayload,
  AccountingCallbackResult,
  AccountingConnectionStatus,
  AccountingConnectResult,
  AccountingProviderId,
  CarryForwardPayload,
  ChartAccountExportQuery,
  ChartAccountQuery,
  ChartAccountRow,
  ChartOfAccountsOptions,
  SaveChartAccountPayload,
  SaveTrialBalancePayload,
  TrialBalanceDetail,
  TrialBalanceLineInput,
  TrialBalanceRow,
} from '@/core/models/trial-balance.models';

const exportWithBlobErrors = (path: string, body: unknown, isPrint: boolean) =>
  exportFile(path, body, isPrint, { unwrapBlobErrors: true });

export const trialBalanceService = {
  // ---- Trial balances ----

  getTrialBalances: (clientId: string) => api.get<TrialBalanceRow[]>(`TrialBalance/getTrialBalances/${clientId}`),

  getNextRefNo: async (clientId: string) => {
    const result = await api.get<{ refNo: string }>(`TrialBalance/getNextRefNo/${clientId}`);
    return result.refNo;
  },

  /**
   * APT340 (reopened): asked at the first step of Create Trial Balance, before any line is entered. Resolves when no other trial balance of the
   * client has exactly these From and To dates; refuses (409, naming the trial balance) when one has. Overlapping periods are fine. Save applies the
   * same rule on the server. APT373: `typeId` (0 Annual, 1 Management only) limits the comparison to trial balances of that type, so a Management
   * only trial balance can share its dates with an Annual one.
   */
  checkDates: (
    clientId: string,
    dates: {
      accountingPeriodId?: string | null;
      fromDate?: string | null;
      toDate?: string | null;
      trialBalanceId?: string | null;
      typeId?: number | null;
    },
  ) => {
    const query = new URLSearchParams();
    if (dates.accountingPeriodId) query.set('accountingPeriodId', dates.accountingPeriodId);
    if (dates.fromDate) query.set('fromDate', dates.fromDate);
    if (dates.toDate) query.set('toDate', dates.toDate);
    if (dates.trialBalanceId) query.set('trialBalanceId', dates.trialBalanceId);
    if (dates.typeId !== null && dates.typeId !== undefined) query.set('type', String(dates.typeId));
    const text = query.toString();
    const suffix = text ? `?${text}` : '';
    return api.get<boolean>(`TrialBalance/checkDates/${clientId}${suffix}`);
  },

  getTrialBalance: (id: string) => api.get<TrialBalanceDetail>(`TrialBalance/getTrialBalance/${id}`),

  saveTrialBalance: (payload: SaveTrialBalancePayload) =>
    api.post<TrialBalanceDetail>('TrialBalance/saveTrialBalance', payload),

  deleteTrialBalances: (ids: string[]) => api.post<boolean>('TrialBalance/deleteTrialBalances', ids),

  postTrialBalance: (id: string) => api.post<TrialBalanceDetail>(`TrialBalance/postTrialBalance/${id}`, {}),

  /**
   * BUG-036: Import Balances - the balances the source trial balance would bring, as lines. Nothing is stored: the
   * editor loads them into the grid unsaved and its Save stores them once they balance.
   */
  previewCarryForward: (clientId: string, payload: CarryForwardPayload) =>
    api.post<TrialBalanceLineInput[]>('TrialBalance/previewCarryForward', { ...payload, clientId }),

  /**
   * BUG-036: a CSV/Excel file (or one exported from QuickBooks, Xero or FreeAgent) read as trial balance lines. Nothing
   * is stored: the editor loads them into the grid unsaved and its Save stores them once they balance.
   * BUG-065/067/068: with the client, old-app account codes are read against its client type and chart of accounts.
   */
  readFile: (file: File, clientId?: string) => {
    const form = new FormData();
    form.append('file', file, file.name);
    const query = clientId ? `?clientId=${encodeURIComponent(clientId)}` : '';
    return api.post<TrialBalanceLineInput[]>(`TrialBalance/readFile${query}`, form);
  },

  /**
   * BUG-038: Mode of Import "Bookkeeping" - the server pulls the period's trial balance from Capium Bookkeeping and
   * creates the trial balance with `trialBalance`'s details and those balances as its lines.
   */
  createFromBookkeeping: (trialBalance: SaveTrialBalancePayload) =>
    api.post<TrialBalanceDetail>('TrialBalance/importFromBookkeeping', { newTrialBalance: trialBalance }),

  // ---- BUG-038: Xero, QuickBooks and FreeAgent (OAuth2 authorization code, handled by the API) ----

  /** Whether the provider is configured for this firm and the client is connected to it. */
  getIntegrationStatus: (provider: AccountingProviderId, clientId: string) =>
    api.get<AccountingConnectionStatus>(`TrialBalance/integrations/${provider}/status`, { params: { clientId } }),

  /**
   * Starts a connection: the provider's consent URL (open it in a popup), or `connected` when the client's saved
   * connection can be used as it is. `reconnect` asks for a new consent anyway.
   */
  connectIntegration: (provider: AccountingProviderId, clientId: string, reconnect = false) =>
    api.post<AccountingConnectResult>(`TrialBalance/integrations/${provider}/connect`, { clientId, reconnect }),

  /** The /oauth/callback page hands what the provider returned (code, state or error) to the API, which stores the tokens. */
  completeIntegrationCallback: (payload: AccountingCallbackPayload) =>
    api.post<AccountingCallbackResult>('TrialBalance/integrations/callback', payload),

  /**
   * The connected provider's trial balance for the new trial balance's period, as lines mapped to the client's chart
   * (accounts that could not be mapped are flagged). Nothing is stored: the editor opens them unsaved.
   */
  importFromIntegration: (provider: AccountingProviderId, trialBalance: SaveTrialBalancePayload) =>
    api.post<TrialBalanceLineInput[]>(`TrialBalance/integrations/${provider}/importTrialBalance`, {
      newTrialBalance: trialBalance,
    }),

  downloadTemplate: async () => {
    try {
      const response = await apiRequest<Blob>('GET', 'TrialBalance/downloadTemplate', { responseType: 'blob' });
      download(response);
      return true;
    } catch (error) {
      return readBlobError(error);
    }
  },

  /** format: 1 CSV, 2 Excel, 3 PDF. */
  exportTrialBalance: (id: string, format: number, isPrint = false) =>
    exportWithBlobErrors(`TrialBalance/exportTrialBalance/${id}`, { format, isPrint }, isPrint),

  // ---- Chart of accounts ----

  /** With a client id the options also carry that client's type, so the form can leave out the account types it may not use. */
  getAccountOptions: (clientId?: string) =>
    api.get<ChartOfAccountsOptions>(
      clientId ? `ChartOfAccounts/getOptions?clientId=${encodeURIComponent(clientId)}` : 'ChartOfAccounts/getOptions',
    ),

  getAccounts: (clientId: string, query: ChartAccountQuery) =>
    api.post<ChartAccountRow[]>(`ChartOfAccounts/getAccounts/${clientId}`, query),

  saveAccount: (payload: SaveChartAccountPayload) => api.post<ChartAccountRow>('ChartOfAccounts/saveAccount', payload),

  deleteAccounts: (ids: string[]) => api.post<boolean>('ChartOfAccounts/deleteAccounts', ids),

  /** Archives (archived true) or restores (false) the accounts. */
  archiveAccounts: (ids: string[], archived: boolean) =>
    api.post<boolean>('ChartOfAccounts/archiveAccounts', { ids, archived }),

  exportAccounts: (clientId: string, query: ChartAccountExportQuery) =>
    exportWithBlobErrors(`ChartOfAccounts/exportAccounts/${clientId}`, query, query.isPrint),
};
